import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { useLang } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import StatusBadge from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { ChevronLeft, ArrowRight, Plus, Trash2, CheckCircle, AlertCircle, Package, FileText } from "lucide-react";
import { DISTRIBUTION } from "@/constants/testIds";
import { FilterTabs } from "@/components/NotificationBanner";
import Toast from "@/components/Toast";
import { useAutoRefresh } from "@/hooks/useAutoRefresh";
import RefreshButton from "@/components/RefreshButton";
import DiscrepancyList from "@/components/DiscrepancyList";
import ItemCorrectionPanel from "@/components/ItemCorrectionPanel";
import InboundPanel, { InboundTabs } from "@/components/inbound/InboundPanel";
import { proportionalShares } from "@/lib/shares";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ax = { withCredentials: true };
const required = (item) => item.effective_quantity ?? item.quantity;

function ProgressBar({ value, max }) {
  const percent = max > 0 ? Math.min(100, (value / max) * 100) : 0;
  const color = percent >= 100 ? "bg-emerald-500" : percent > 0 ? "bg-blue-500" : "bg-slate-200";
  return (
    <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
      <div className={`h-full rounded-full transition-all ${color}`} style={{ width: `${percent}%` }} />
    </div>
  );
}

function WorkStatus({ status, t }) {
  const styles = {
    PLANNING: "bg-blue-50 text-blue-700 border-blue-200",
    SUBMITTED: "bg-amber-50 text-amber-700 border-amber-200",
    BOXING: "bg-cyan-50 text-cyan-700 border-cyan-200",
    BOXED: "bg-emerald-50 text-emerald-700 border-emerald-200",
    COMPLETED: "bg-emerald-100 text-emerald-800 border-emerald-300",
  };
  return (
    <span className={`inline-flex rounded-full border px-2.5 py-1 text-xs font-semibold ${styles[status] || "bg-slate-50 text-slate-700 border-slate-200"}`}>
      {t(`status.${status}`)}
    </span>
  );
}

export default function Planning() {
  const { t } = useLang();
  const { user } = useAuth();
  const isPlatform = user?.role === "TOOLS_USER";
  const [tenants, setTenants] = useState([]);
  const [tenantId, setTenantId] = useState("all");
  const [works, setWorks] = useState([]);
  const [selected, setSelected] = useState(null);
  const [items, setItems] = useState([]);
  const [distributions, setDistributions] = useState([]);
  const [revisions, setRevisions] = useState([]);
  const [discrepancies, setDiscrepancies] = useState([]);
  const [extraDraft, setExtraDraft] = useState({});
  const [filterMode, setFilterMode] = useState("active");
  const [loading, setLoading] = useState(true);
  const [distModal, setDistModal] = useState(null);
  const [distStore, setDistStore] = useState("");
  const [distQty, setDistQty] = useState(1);
  const [fnskuStores, setFnskuStores] = useState([]);
  const [fnskuLoading, setFnskuLoading] = useState(false);
  const [confirmSubmit, setConfirmSubmit] = useState(false);
  const [msg, setMsg] = useState(null);
  const [tab, setTab] = useState("pallets"); // "pallets" | "inbound" | "stock"
  const [revising, setRevising] = useState(null); // { id, quantity, note } of the item in revise mode

  const showMsg = (type, text) => setMsg({ type, text });

  useEffect(() => {
    if (!isPlatform) return;
    axios.get(`${API}/tenants`, ax).then(({ data }) => setTenants(data)).catch(() => {
      showMsg("error", t("planning.error"));
    });
  }, [isPlatform, t]);

  const fetchAllWork = useCallback(async () => {
    const params = {};
    if (isPlatform && tenantId !== "all") params.tenant_id = tenantId;
    const { data } = await axios.get(`${API}/pallet-work`, { ...ax, params });
    return data;
  }, [isPlatform, tenantId]);

  // Work with differences to acknowledge stays active even while boxing.
  const isActive = work => work.status === "PLANNING" || work.has_open_discrepancy;
  const visibleWork = data => data.filter(work => (filterMode === "active" ? isActive(work) : !isActive(work)));

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const data = await fetchAllWork();
        if (!cancelled) setWorks(visibleWork(data));
      } catch {
        if (!cancelled) showMsg("error", t("planning.loading_error"));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [filterMode, fetchAllWork, t]);

  const loadWorkDetails = async (work) => {
    const [details, revisionResult, discrepancyResult] = await Promise.all([
      axios.get(`${API}/pallet-work/${work.id}/planning-data`, ax),
      axios.get(`${API}/pallet-work/${work.id}/revisions`, ax).catch(() => ({ data: [] })),
      axios.get(`${API}/pallet-work/${work.id}/discrepancies`, ax).catch(() => ({ data: [] })),
    ]);
    setItems(details.data.items);
    setDistributions(details.data.distributions);
    setRevisions(revisionResult.data);
    setDiscrepancies(discrepancyResult.data);
  };

  const openWork = async (work) => {
    setSelected(work);
    setItems([]);
    setDistributions([]);
    setRevisions([]);
    setDiscrepancies([]);
    setExtraDraft({});
    setRevising(null);
    try {
      await loadWorkDetails(work);
    } catch {
      showMsg("error", t("planning.detail_error"));
    }
  };

  const refreshSelected = async () => {
    if (!selected) return;
    await loadWorkDetails(selected);
  };

  // Background and manual refresh: the list and, when a work is open, its status and details.
  const reloadScreen = async () => {
    const data = await fetchAllWork();
    setWorks(visibleWork(data));
    if (!selected) return;
    const fresh = data.find(work => work.id === selected.id);
    if (fresh) setSelected(fresh);
    await loadWorkDetails(selected);
  };
  const { refresh, refreshing } = useAutoRefresh(reloadScreen, { paused: !!distModal || confirmSubmit || tab !== "pallets" });

  const getDistTotal = (asin) => distributions
    .filter(distribution => distribution.asin === asin)
    .reduce((total, distribution) => total + distribution.quantity, 0);

  // A store that already has a distribution of this product is changed with the +/- buttons, not added again.
  const unusedStores = (stores, asin) => stores.filter(store =>
    !distributions.some(distribution => distribution.asin === asin && distribution.store_id === store.store_id));
  const freeStores = distModal ?unusedStores(fnskuStores, distModal.asin) : [];
  const openDiscrepancies = discrepancies.filter(row => row.status === "OPEN");
  const allDistributed = items.length > 0 && items.every(item => getDistTotal(item.asin) === required(item));
  const openRevisions = revisions.filter(revision => revision.status === "OPEN");
  const editable = selected?.status === "PLANNING" && !isPlatform;
  const correcting = selected?.status === "BOXING" && !isPlatform;
  // The sender's mistake is fixed in the pallet items while the pallet is not received yet (backend: correctable_work).
  const canCorrectItems = editable && openRevisions.length > 0 && ["READY", "IN_TRANSIT"].includes(selected?.physical_status);
  // The tenant re-counts its own line (backend: revisable_work); no request from the warehouse is needed.
  const canRevise = editable && ["READY", "IN_TRANSIT"].includes(selected?.physical_status);
  const wasRevised = item => item.original_quantity != null && item.original_quantity !== item.quantity;
  const overPlanned = items.filter(item => getDistTotal(item.asin) > required(item));
  const productNames = Object.fromEntries(items.map(item => [item.asin, item.product_name]));
  const preliminary = selected && ["READY", "IN_TRANSIT"].includes(selected.physical_status)
    && ["PLANNING", "SUBMITTED"].includes(selected.status);

  const acknowledge = async () => {
    const accepted = Object.fromEntries(Object.entries(extraDraft)
      .map(([itemId, value]) => [itemId, Number.parseInt(value, 10) || 0]).filter(([, value]) => value > 0));
    try {
      await axios.post(`${API}/pallet-work/${selected.id}/discrepancies/acknowledge`, { accepted_extra: accepted }, ax);
      setExtraDraft({});
      await refreshSelected();
      showMsg("success", t("planning.acknowledged"));
    } catch (error) {
      showMsg("error", error.response?.data?.detail || t("planning.error"));
    }
  };

  // The tenant triggers this explicitly; it only ever lowers allocations toward the usable quantity.
  const reduceProportionally = async () => {
    try {
      for (const item of overPlanned) {
        const rows = distributions.filter(d => d.asin === item.asin);
        const shares = proportionalShares(rows.map(d => d.quantity), required(item));
        for (let index = 0; index < rows.length; index += 1) {
          if (shares[index] === rows[index].quantity) continue;
          if (shares[index] === 0) {
            await axios.delete(`${API}/pallets/${selected.pallet_id}/distributions/${rows[index].id}`, ax);
          } else {
            await axios.post(`${API}/pallets/${selected.pallet_id}/distributions`, {
              asin: item.asin, store_id: rows[index].store_id, quantity: shares[index],
            }, ax);
          }
        }
      }
      showMsg("success", t("planning.proportional_done"));
    } catch (error) {
      showMsg("error", error.response?.data?.detail || t("planning.error"));
    }
    await refreshSelected();
  };

  const toggleRevise = item => setRevising(current =>
    current?.id === item.id ? null : { id: item.id, quantity: String(item.quantity), note: item.note || "" });

  const saveRevise = async () => {
    const quantity = Number.parseInt(revising.quantity, 10);
    if (!(quantity >= 1)) return;
    try {
      await axios.put(`${API}/pallet-work/${selected.id}/items/${revising.id}/revise`, { quantity, note: revising.note }, ax);
      setRevising(null);
      await refreshSelected();
      showMsg("success", t("planning.revise_done"));
    } catch (error) {
      showMsg("error", error.response?.data?.detail || t("planning.error"));
    }
  };

  const openDistModal = async (item) => {
    setDistModal(item);
    setDistStore("");
    setDistQty(1);
    setFnskuLoading(true);
    try {
      const { data } = await axios.get(`${API}/store-fnsku?asin=${encodeURIComponent(item.asin)}`, ax);
      setFnskuStores(data);
      const free = unusedStores(data, item.asin);
      if (free.length === 1) setDistStore(free[0].store_id);
    } catch {
      setFnskuStores([]);
    } finally {
      setFnskuLoading(false);
    }
  };

  const saveDistribution = async () => {
    if (!distModal || !distStore || distQty < 1) return;
    try {
      await axios.post(`${API}/pallets/${selected.pallet_id}/distributions`, {
        asin: distModal.asin, store_id: distStore, quantity: distQty,
      }, ax);
      await refreshSelected();
      setDistModal(null);
      showMsg("success", t("planning.quantity_added"));
    } catch (error) {
      showMsg("error", error.response?.data?.detail || t("planning.error"));
    }
  };

  const updateDistribution = async (distribution, quantity) => {
    if (quantity < 1) return;
    try {
      await axios.post(`${API}/pallets/${selected.pallet_id}/distributions`, {
        asin: distribution.asin, store_id: distribution.store_id, quantity,
      }, ax);
      await refreshSelected();
      showMsg("success", t("planning.quantity_updated"));
    } catch (error) {
      showMsg("error", error.response?.data?.detail || t("planning.error"));
    }
  };

  const deleteDistribution = async (distribution) => {
    try {
      await axios.delete(`${API}/pallets/${selected.pallet_id}/distributions/${distribution.id}`, ax);
      await refreshSelected();
      showMsg("success", t("planning.quantity_removed"));
    } catch (error) {
      showMsg("error", error.response?.data?.detail || t("planning.error"));
    }
  };

  const resolveRevision = async (revisionId) => {
    try {
      await axios.post(`${API}/pallet-work/${selected.id}/revisions/${revisionId}/resolve`, {}, ax);
      await refreshSelected();
      showMsg("success", t("planning.revision_resolved"));
    } catch (error) {
      showMsg("error", error.response?.data?.detail || t("planning.error"));
    }
  };

  const submitPlanning = async () => {
    try {
      const { data } = await axios.post(`${API}/pallet-work/${selected.id}/submit-planning`, {}, ax);
      const updated = { ...selected, ...data };
      setSelected(updated);
      setWorks(current => current.filter(work => work.id !== selected.id));
      setConfirmSubmit(false);
      showMsg("success", t("planning.submit_success"));
    } catch (error) {
      setConfirmSubmit(false);
      showMsg("error", error.response?.data?.detail || t("planning.submit_error"));
    }
  };

  if (selected) {
    const readOnly = (selected.status !== "PLANNING" && !correcting) || isPlatform;
    return (
      <div className="space-y-4 pb-20">
        <Toast msg={msg} onClose={() => setMsg(null)} />
        <div className="flex items-center gap-3 flex-wrap">
          <Button variant="outline" size="sm" onClick={() => setSelected(null)} aria-label={t("planning.back")} className="gap-1.5">
            <ChevronLeft className="w-4 h-4" /> {t("planning.back")}
          </Button>
          <div className="flex-1 min-w-0">
            <h1 className="text-xl font-bold text-slate-900 truncate">{selected.pallet_name}</h1>
            <div className="mt-1 flex flex-wrap items-center gap-2">
              <WorkStatus status={selected.status} t={t} />
              {isPlatform && <span className="text-sm text-slate-500">{selected.tenant_name}</span>}
            </div>
          </div>
          <RefreshButton onClick={refresh} refreshing={refreshing} />
          {editable && (
            <Button size="sm" disabled={!allDistributed || openRevisions.length > 0 || openDiscrepancies.length > 0}
              onClick={() => setConfirmSubmit(true)} className="gap-1.5 bg-blue-600 hover:bg-blue-700">
              <ArrowRight className="w-4 h-4" /> {t("planning.submit")}
            </Button>
          )}
        </div>

        {readOnly && (
          <div className="flex items-center gap-2 p-3 bg-slate-100 border border-slate-200 rounded-lg text-slate-600 text-sm">
            <FileText className="w-4 h-4 shrink-0" />
            {isPlatform ? t("planning.platform_read_only") : t("planning.read_only")}
          </div>
        )}
        <div className="flex flex-wrap items-center gap-2 text-sm text-slate-600">
          <span>{t("planning.arrival")}:</span>
          <StatusBadge status={selected.physical_status} />
          <span className="text-slate-400">{selected.destination_warehouse_name}</span>
        </div>

        {openRevisions.map(revision => (
          <div key={revision.id} className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-lg">
            <AlertCircle className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="font-semibold text-red-800 text-sm">{t("planning.revision_open")}</p>
              <p className="text-xs text-red-500 mt-0.5">
                {new Date(revision.requested_at).toLocaleString()} · {revision.requested_by_name}
              </p>
              <p className="text-sm text-red-700 mt-1">{revision.note}</p>
            </div>
            {!isPlatform && (
              <Button size="sm" onClick={() => resolveRevision(revision.id)} className="shrink-0 gap-1.5 bg-emerald-600 hover:bg-emerald-700">
                <CheckCircle className="w-4 h-4" /> {t("planning.resolve_revision")}
              </Button>
            )}
          </div>
        ))}

        {canCorrectItems && (
          <ItemCorrectionPanel workId={selected.id} items={items} onChanged={refreshSelected}
            onMessage={showMsg} />
        )}

        {preliminary && (
          <div className="flex items-center gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg text-blue-800 text-sm">
            <AlertCircle className="w-4 h-4 shrink-0" /> {t("planning.preliminary")}
          </div>
        )}
        {correcting && overPlanned.length > 0 && (
          <div className="flex items-center gap-2 p-3 bg-slate-100 border border-slate-200 rounded-lg text-slate-600 text-sm">
            <AlertCircle className="w-4 h-4 shrink-0" /> {t("planning.correction_mode")}
          </div>
        )}

        {discrepancies.length > 0 && (
          <div className="space-y-3" data-testid="planning-discrepancies">
            {openDiscrepancies.length > 0 && (
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="font-semibold text-amber-900 text-sm">{t("planning.discrepancy_title")}</p>
                <p className="text-sm text-amber-800 mt-1">{t("planning.discrepancy_body")}</p>
              </div>
            )}
            <DiscrepancyList rows={discrepancies} names={productNames} actions={row => {
              const max = row.source === "RECEIPT" ? Math.max(0, row.good - row.declared) : 0;
              if (row.status !== "OPEN" || max === 0 || isPlatform) return null;
              return (
                <label className="flex items-center gap-2 text-xs text-slate-600 flex-wrap">
                  {t("planning.accept_extra", { max })}
                  <Input inputMode="numeric" className="h-9 w-20" value={extraDraft[row.item_id] ?? ""} placeholder="0"
                    onChange={event => setExtraDraft(draft => ({
                      ...draft, [row.item_id]: String(Math.min(max, Number.parseInt(event.target.value.replace(/\D/g, ""), 10) || 0) || ""),
                    }))} />
                </label>
              );
            }} />
            {openDiscrepancies.length > 0 && !isPlatform && (
              <Button size="sm" onClick={acknowledge} className="gap-1.5 bg-amber-600 hover:bg-amber-700" data-testid="acknowledge-discrepancies">
                <CheckCircle className="w-4 h-4" /> {t("planning.acknowledge")}
              </Button>
            )}
          </div>
        )}
        {(editable || correcting) && overPlanned.length > 0 && openDiscrepancies.length === 0 && (
          <div className="flex items-center gap-3 flex-wrap p-3 bg-white border border-slate-200 rounded-lg">
            <p className="flex-1 text-sm text-slate-600 min-w-0">{t("planning.proportional_hint")}</p>
            <Button size="sm" variant="outline" onClick={reduceProportionally} data-testid="reduce-proportionally">{t("planning.proportional")}</Button>
          </div>
        )}

        {editable && !allDistributed && (
          <div className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg text-amber-800 text-sm">
            <AlertCircle className="w-4 h-4 shrink-0" /> {t("planning.submit_blocked")}
          </div>
        )}

        <div className="space-y-4">
          {items.map(item => {
            const total = getDistTotal(item.asin);
            const target = required(item);
            const remaining = target - total;
            const over = total > target;
            const canLower = editable || (correcting && over);
            const itemDistributions = distributions.filter(distribution => distribution.asin === item.asin);
            const complete = remaining === 0;
            const revisingThis = revising?.id === item.id;
            const flagged = wasRevised(item) || !!item.note;
            return (
              <section key={item.id} className={`rounded-lg border ${flagged ? "bg-amber-50/50 border-amber-300" : "bg-white border-slate-200"}`}>
                <div className="p-4 border-b border-slate-100">
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <div className="min-w-0">
                      <h2 className="font-semibold text-slate-900">{item.product_name}</h2>
                      <p className="text-xs font-mono text-slate-500">{item.asin}</p>
                      {item.received_good != null && (item.received_good !== item.quantity || item.damaged_qty > 0 || item.unexpected) && (
                        <p className="text-xs text-amber-700 mt-0.5">
                          {t("planning.counted", { declared: item.quantity, good: item.received_good, damaged: item.damaged_qty || 0 })}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className="text-sm font-bold text-slate-700">{total}/{target}</span>
                      {canRevise && (
                        <button type="button" onClick={() => toggleRevise(item)} title={t("planning.revise_tip")}
                          aria-label={t("planning.revise_tip")} aria-pressed={revisingThis}
                          className={`flex h-7 w-7 items-center justify-center rounded border text-xs font-bold transition-colors ${
                            revisingThis ? "border-red-600 bg-red-600 text-white" : "border-slate-300 bg-white text-slate-600 hover:bg-slate-100"}`}>
                          R
                        </button>
                      )}
                    </div>
                  </div>
                  {flagged && !revisingThis && (
                    <p className="mb-2 text-xs font-medium text-amber-800">
                      <span className="mr-1.5 rounded bg-amber-200 px-1.5 py-px font-bold">
                        {wasRevised(item) ? t("planning.revised_was", { count: item.original_quantity }) : t("planning.revised")}
                      </span>
                      {item.note}
                    </p>
                  )}
                  {revisingThis && (
                    <div className="mb-3 space-y-2 rounded-lg border border-red-200 bg-red-50/40 p-3">
                      <div className="flex items-center gap-2">
                        <Button variant="outline" size="icon" className="h-9 w-9" aria-label={t("planning.quantity")}
                          disabled={!(Number.parseInt(revising.quantity, 10) > 1)}
                          onClick={() => setRevising(r => ({ ...r, quantity: String(Math.max(1, (Number.parseInt(r.quantity, 10) || 1) - 1)) }))}>−</Button>
                        <Input type="text" inputMode="numeric" pattern="[0-9]*" value={revising.quantity}
                          onChange={event => /^\d*$/.test(event.target.value) && setRevising(r => ({ ...r, quantity: event.target.value }))}
                          onFocus={event => event.target.select()} aria-label={t("planning.quantity")}
                          className="h-9 w-20 text-center font-semibold" />
                        <Button variant="outline" size="icon" className="h-9 w-9" aria-label={t("planning.quantity")}
                          onClick={() => setRevising(r => ({ ...r, quantity: String((Number.parseInt(r.quantity, 10) || 0) + 1) }))}>+</Button>
                        <Button size="sm" className="ml-auto h-9" onClick={saveRevise}
                          disabled={!(Number.parseInt(revising.quantity, 10) >= 1)}>{t("planning.revise_save")}</Button>
                      </div>
                      <Input value={revising.note} maxLength={200} placeholder={t("planning.revise_note")}
                        onChange={event => setRevising(r => ({ ...r, note: event.target.value }))} className="h-9" />
                    </div>
                  )}
                  <ProgressBar value={total} max={target} />
                  <div className="flex items-center justify-between gap-3 mt-2">
                    <span className={`text-xs font-medium ${complete ? "text-emerald-700" : "text-amber-700"}`}>
                      {complete ? t("planning.distributed") : t("planning.remaining", { count: remaining })}
                    </span>
                    {editable && remaining > 0 && (
                      <Button size="sm" variant="outline" onClick={() => openDistModal(item)}
                        data-testid={DISTRIBUTION.addBtn} className="h-8 gap-1 text-xs">
                        <Plus className="w-3.5 h-3.5" /> {t("planning.add_store")}
                      </Button>
                    )}
                  </div>
                </div>
                {itemDistributions.length > 0 && (
                  <div className="divide-y divide-slate-100">
                    {itemDistributions.map(distribution => (
                      <div key={distribution.id} className="flex items-center gap-3 px-4 py-3">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-slate-800">{distribution.store_name}</p>
                          {distribution.fnsku && <p className="text-xs font-mono text-slate-500">{distribution.fnsku}</p>}
                          {distribution.boxed > 0 && canLower && (
                            <p className="text-xs font-medium text-blue-700">{t("planning.boxed_note", { count: distribution.boxed })}</p>
                          )}
                          {distribution.boxed > distribution.quantity && (
                            <p className="text-xs font-medium text-amber-800">
                              <span className="mr-1.5 rounded bg-amber-200 px-1.5 py-px font-bold">{t("recv.over_badge", { count: distribution.boxed - distribution.quantity })}</span>
                              {t("planning.over_note", { count: distribution.boxed - distribution.quantity })}
                              {(distribution.over_reasons || []).map(r => ` ${t(`recv.over_r.${r.reason}`)}${r.note ? ` · ${r.note}` : ""}`).join(";")}
                            </p>
                          )}
                        </div>
                        {canLower ? (
                          <>
                            <Button variant="outline" size="icon" className="h-9 w-9" aria-label={t("planning.quantity")}
                              onClick={() => updateDistribution(distribution, distribution.quantity - 1)}
                              disabled={distribution.quantity <= Math.max(1, distribution.boxed || 0)}>−</Button>
                            <span className="w-10 text-center text-sm font-semibold text-slate-700" aria-label={t("planning.quantity")}>
                              {distribution.quantity}
                            </span>
                            {editable && (
                              <Button variant="outline" size="icon" className="h-9 w-9" aria-label={t("planning.quantity")}
                                onClick={() => updateDistribution(distribution, distribution.quantity + 1)}>+</Button>
                            )}
                            <Button variant="ghost" size="icon" className="h-9 w-9 text-red-500"
                              disabled={distribution.boxed > 0}
                              onClick={() => deleteDistribution(distribution)} aria-label={t("planning.delete_allocation")}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </>
                        ) : <span className="font-semibold text-slate-700">×{distribution.quantity}</span>}
                      </div>
                    ))}
                    {editable && remaining > 0 && (
                      <div className="px-4 py-2">
                        <Button size="sm" variant="ghost" onClick={() => openDistModal(item)} className="h-8 gap-1 text-xs">
                          <Plus className="w-3.5 h-3.5" /> {t("planning.add_store_more")}
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              </section>
            );
          })}
        </div>

        {revisions.length > 0 && (
          <details className="bg-white rounded-lg border border-slate-200">
            <summary className="cursor-pointer px-4 py-3 text-sm font-semibold text-slate-700">
              {t("planning.revision_history", { count: revisions.length })}
            </summary>
            <div className="divide-y divide-slate-100 border-t border-slate-100">
              {revisions.map(revision => (
                <div key={revision.id} className="px-4 py-3">
                  <p className="text-xs text-slate-500">
                    {t(`planning.revision_status.${revision.status.toLowerCase()}`)} · {new Date(revision.requested_at).toLocaleString()} · {revision.requested_by_name}
                  </p>
                  <p className="mt-1 text-sm text-slate-800">{revision.note}</p>
                </div>
              ))}
            </div>
          </details>
        )}

        <Dialog open={!!distModal} onOpenChange={open => { if (!open) setDistModal(null); }}>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle>{t("planning.modal_title", { name: distModal?.product_name || "" })}</DialogTitle>
              <p className="text-sm text-muted-foreground">
                {t("planning.remaining", { count: distModal ? required(distModal) - getDistTotal(distModal.asin) : 0 })}
              </p>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-1.5">
                <Label>{t("common.store")} *</Label>
                {!fnskuLoading && fnskuStores.length === 0 ? (
                  <div className="space-y-1 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
                    <p>{t("planning.no_fnsku")}</p>
                    <p>{t("planning.add_fnsku_hint")}</p>
                  </div>
                ) : !fnskuLoading && freeStores.length === 0 ? (
                  <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
                    <p>{t("planning.all_stores_used")}</p>
                  </div>
                ) : (
                  <Select value={distStore} onValueChange={setDistStore} disabled={fnskuLoading}>
                    <SelectTrigger className="h-11"><SelectValue placeholder={fnskuLoading ? t("planning.loading_stores") : t("planning.select_store")} /></SelectTrigger>
                    <SelectContent>
                      {freeStores.map(store => (
                        <SelectItem key={store.store_id} value={store.store_id}>
                          {store.store_name} <span className="ml-2 text-xs text-slate-400 font-mono">{store.fnsku}</span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>
              <div className="space-y-1.5">
                <Label>{t("planning.quantity")} *</Label>
                <div className="flex items-center gap-2">
                  <Button variant="outline" className="h-11 w-11 p-0 text-lg" onClick={() => setDistQty(Math.max(1, distQty - 1))}>−</Button>
                  <Input type="number" min="1" value={distQty} onChange={event => setDistQty(Number.parseInt(event.target.value, 10) || 1)}
                    className="h-11 flex-1 text-center" data-testid={DISTRIBUTION.quantityInput} />
                  <Button variant="outline" className="h-11 w-11 p-0 text-lg" onClick={() => setDistQty(distQty + 1)}>+</Button>
                </div>
                <Button variant="ghost" size="sm" className="text-xs text-blue-600"
                  onClick={() => distModal && setDistQty(required(distModal) - getDistTotal(distModal.asin))}>
                  {t("planning.set_max")}
                </Button>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDistModal(null)}>{t("planning.cancel")}</Button>
              <Button onClick={saveDistribution} disabled={!distStore || distQty < 1 || fnskuLoading || freeStores.length === 0}
                className="bg-blue-600 hover:bg-blue-700">{t("planning.add")}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <AlertDialog open={confirmSubmit} onOpenChange={setConfirmSubmit}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{t("planning.submit_title")}</AlertDialogTitle>
              <p className="text-sm text-muted-foreground">{t("planning.submit_body")}</p>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>{t("planning.cancel")}</AlertDialogCancel>
              <AlertDialogAction onClick={submitPlanning} className="bg-blue-600 hover:bg-blue-700">{t("planning.submit")}</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    );
  }

  return (
    <div className="space-y-4 pb-4">
      <Toast msg={msg} onClose={() => setMsg(null)} />
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="text-2xl font-extrabold text-slate-900" style={{ fontFamily: "var(--font-heading)" }}>{t("nav.planning")}</h1>
        {tab === "pallets" && isPlatform && (
          <Select value={tenantId} onValueChange={setTenantId}>
            <SelectTrigger className="w-full sm:w-64"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("planning.all_tenants")}</SelectItem>
              {tenants.map(tenant => <SelectItem key={tenant.id} value={tenant.id}>{tenant.name}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        {tab === "pallets" && (
          <div className="flex items-center gap-2">
            <FilterTabs mode={filterMode} onChange={setFilterMode}
              labels={{ active: t("planning.active"), history: t("planning.history") }} />
            <RefreshButton onClick={refresh} refreshing={refreshing} />
          </div>
        )}
      </div>
      {/* Inbound rows are entered by tenant users with the tenant's Inbound switch on; the platform view stays on pallets. */}
      {!isPlatform && user?.membership?.inbound_enabled && <InboundTabs value={tab} onChange={setTab} />}

      {tab !== "pallets" ? (
        <InboundPanel side="tenant" view={tab} />
      ) : loading ? (
        <div className="py-12 text-center text-sm text-slate-500">{t("common.loading")}</div>
      ) : works.length === 0 ? (
        <div className="rounded-lg border border-slate-200 bg-white p-10 text-center text-slate-500">
          <Package className="mx-auto mb-3 h-10 w-10 text-slate-300" />
          <p className="font-semibold text-slate-700">{t("planning.empty_title")}</p>
          <p className="mt-1 text-sm">{filterMode === "active" ? t("planning.empty_active") : t("planning.empty_history")}</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {works.map(work => (
            <button key={work.id} type="button" onClick={() => openWork(work)}
              className="rounded-lg border border-slate-200 bg-white p-4 text-left transition-colors hover:border-blue-300 hover:bg-blue-50/30">
              <div className="mb-3 flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <h2 className="truncate font-semibold text-slate-900">{work.pallet_name}</h2>
                  <p className="mt-1 text-xs text-slate-500">{new Date(work.created_at).toLocaleDateString()}</p>
                  {isPlatform && <p className="mt-1 text-xs text-slate-600">{work.tenant_name}</p>}
                </div>
                <WorkStatus status={work.status} t={t} />
              </div>
              <div className="flex flex-wrap items-center gap-2 text-sm text-slate-600">
                <Package className="h-4 w-4" />
                <span>{t("planning.items", { count: work.item_count })}</span>
                <span className="text-slate-300">·</span>
                <span>{t("planning.units", { count: work.total_quantity })}</span>
              </div>
              {work.has_revision_request && (
                <p className="mt-2 flex items-center gap-1.5 text-xs font-semibold text-red-600">
                  <AlertCircle className="h-3.5 w-3.5" /> {t("planning.revision_open")}
                </p>
              )}
              {work.has_open_discrepancy && (
                <p className="mt-2 flex items-center gap-1.5 text-xs font-semibold text-amber-700">
                  <AlertCircle className="h-3.5 w-3.5" /> {t("planning.discrepancy_open")}
                </p>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}